import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FolderOpen, MessageSquareText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ChatAttachment } from '@hub/contracts'
import { ChatComposer } from '@/components/chat/chat-composer'
import { useOptionalProject } from '@/lib/project-context'
import { orpc } from '@/lib/orpc-client'
import { Button } from '@/components/ui/button'

interface ChatInterfaceProps {
  onNewChat: (message: string, attachments?: ChatAttachment[]) => void
  isSending?: boolean
  draftKey?: { projectId: string; threadKey: string }
}

/** New-chat landing: project heading + composer. */
export function ChatInterface({ onNewChat, isSending, draftKey }: ChatInterfaceProps) {
  const projectCtx = useOptionalProject()
  const { t } = useTranslation('chat')
  const project = projectCtx?.project
  // The landing is only routed under /$projectId/chat, so this is the loading
  // frame, not a real state — and the composer never runs without a project.
  if (!project) return null
  // Board 5q82: until the project has a description, owners and admins are
  // offered the onboarding conversation (hidden once the agent saved one, or
  // once someone skipped the whole onboarding).
  const offerOnboarding =
    !project.description?.trim() &&
    !project.onboardingSkippedAt &&
    (project.role === 'owner' || project.role === 'admin')

  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="w-full max-w-3xl space-y-8">
        <div className="space-y-4 text-center">
          <h1 className="text-foreground text-5xl font-bold">HUB</h1>
          <h2 className="text-muted-foreground flex items-center justify-center gap-2 text-2xl font-medium text-balance">
            <FolderOpen className="h-6 w-6" />
            {project.name}
          </h2>
        </div>

        {offerOnboarding && <OnboardingCard projectId={project.id} />}

        <ChatComposer
          projectId={project.id}
          onSend={onNewChat}
          placeholder={t('composer.placeholderNew')}
          disabled={isSending}
          draftKey={draftKey}
          resetModeToNormal
          autoFocus
        />
      </div>
    </div>
  )
}

/** Starts (or reopens) the project onboarding chat — `chat.startOnboarding` is idempotent. */
function OnboardingCard({ projectId }: { projectId: string }) {
  const { t } = useTranslation('chat')
  const navigate = useNavigate()
  const [starting, setStarting] = useState(false)
  const [failed, setFailed] = useState(false)

  const start = async () => {
    setStarting(true)
    setFailed(false)
    try {
      const { thread } = await orpc.chat.startOnboarding({ projectId })
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
      void navigate({ to: '/$projectId/chat/$chatId', params: { projectId, chatId: thread.id } })
    } catch {
      setFailed(true)
      setStarting(false)
    }
  }

  return (
    <div
      data-slot="onboarding-card"
      className="border-border bg-muted/40 flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center"
    >
      <MessageSquareText className="text-muted-foreground hidden h-5 w-5 shrink-0 sm:block" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-foreground text-sm font-medium">{t('onboarding.title')}</p>
        <p className="text-muted-foreground text-sm">
          {failed ? t('onboarding.failed') : t('onboarding.body')}
        </p>
      </div>
      <Button size="sm" onClick={start} disabled={starting} className="self-start sm:self-auto">
        {starting ? t('onboarding.starting') : t('onboarding.start')}
      </Button>
    </div>
  )
}
